import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiRobotOffOutline } from '@mdi/js'
import { useJobsStatus } from '../../api/jobs'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { useIsPlatformAdmin } from '../../hooks/useAuth'
import { Alert, AlertDescription } from '../ui/alert'

/**
 * AIProviderBanner is the one line the jobs page was missing (spec
 * 2026-09-10-03 §3).
 *
 * A dead Gemini credential used to show up here as a wall of red rows carrying
 * raw provider JSON, and nothing else — the reporter of #1322 read that as
 * "classification is broken", which it was not. This says what is actually
 * wrong, since when, how much is waiting, and when we will look again.
 *
 * Non-blocking notification, never a toast: the state it describes lasts until
 * someone replaces a key, which is longer than any toast should live.
 */
export function AIProviderBanner({ org }: { org: string }) {
    const { t } = useTranslation('jobs')
    useLiveSubscription({ entity: 'jobs' })
    const isJobsScopeLive = useScopeLive({ entity: 'jobs' })
    const { data } = useJobsStatus(org, isJobsScopeLive)
    const isPlatformAdmin = useIsPlatformAdmin()

    const provider = data?.ai_provider
    // `ok` and an unwired provider both mean "nothing to say here".
    if (!provider || provider.state === 'ok') return null

    const headline =
        provider.state === 'rejected_key'
            ? t('ai_provider.banner_rejected_key', {
                  reason: provider.reason || '—',
                  date: provider.since ? new Date(provider.since).toLocaleString() : '—',
              })
            : t('ai_provider.banner_missing_key')

    return (
        <Alert variant="warning" className="mb-4" data-testid="jobs-ai-provider-banner">
            <AlertDescription className="flex flex-wrap items-center gap-2">
                <AppIcon path={mdiRobotOffOutline} size="md" />
                <span data-testid="jobs-ai-provider-banner-headline">{headline}</span>
                {provider.deferred_jobs > 0 && (
                    <span>{t('ai_provider.banner_waiting', { count: provider.deferred_jobs })}</span>
                )}
                {provider.next_probe_at && (
                    <span data-testid="jobs-ai-provider-banner-next-probe">
                        {t('ai_provider.banner_next_probe', {
                            time: new Date(provider.next_probe_at).toLocaleTimeString(),
                        })}
                    </span>
                )}
                {/*
                  The same CTA as the blocker: the fix lives in the admin
                  settings, and only a platform admin can reach them — everyone
                  else gets the sentence that tells them who to ask, rather
                  than a link that would 403.
                */}
                {isPlatformAdmin ? (
                    <Link
                        to="/admin/settings"
                        className="underline font-medium"
                        data-testid="jobs-ai-provider-banner-cta"
                    >
                        {t('ai_provider.row_cta')}
                    </Link>
                ) : (
                    <span className="text-muted-foreground" data-testid="jobs-ai-provider-banner-cta-hint">
                        {t('ai_provider.row_cta_hint')}
                    </span>
                )}
                <Link
                    to="/$org/jobs/blockers"
                    params={{ org }}
                    className="underline font-medium"
                    data-testid="jobs-ai-provider-banner-link"
                >
                    {t('ai_provider.banner_link')}
                </Link>
            </AlertDescription>
        </Alert>
    )
}
